import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Camera, ChevronLeft, ChevronRight } from 'lucide-react';

const HappyCustomersGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/happy-customers?randomize=true`);
      setPhotos(res.data);
    } catch (error) {
      console.error('Failed to fetch happy customer photos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (photos.length === 0) {
    return null;
  }

  const displayPhotos = photos.slice(0, 8); // Show max 8 photos

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % displayPhotos.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + displayPhotos.length) % displayPhotos.length);
  };

  return (
    <section className="py-16 bg-[#0a1628]" data-testid="happy-customers-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Exo_2']">
            Our <span className="gradient-text">Happy Customers</span>
          </h2>
          <p className="text-[#a8b8d0] text-lg max-w-2xl mx-auto">
            Join the family of satisfied customers who found their perfect vehicle at We Have Your Car.
          </p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-4 gap-4">
          {displayPhotos.map((photo, index) => (
            <div
              key={photo.id}
              className="group relative aspect-square rounded-xl overflow-hidden bg-[#132238] border border-[#1a2d47] hover:border-[#ff6b35] transition-all"
            >
              <img
                src={photo.image_url}
                alt={photo.caption || 'Happy customer'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              {(photo.customer_name || photo.caption) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  {photo.customer_name && (
                    <p className="text-white font-medium text-sm">{photo.customer_name}</p>
                  )}
                  {photo.caption && (
                    <p className="text-[#a8b8d0] text-xs">{photo.caption}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <div className="overflow-hidden rounded-xl">
            <div className="aspect-square relative">
              <img
                src={displayPhotos[currentIndex]?.image_url}
                alt={displayPhotos[currentIndex]?.caption || 'Happy customer'}
                className="w-full h-full object-cover"
              />
              {(displayPhotos[currentIndex]?.customer_name || displayPhotos[currentIndex]?.caption) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  {displayPhotos[currentIndex]?.customer_name && (
                    <p className="text-white font-medium">{displayPhotos[currentIndex].customer_name}</p>
                  )}
                  {displayPhotos[currentIndex]?.caption && (
                    <p className="text-[#a8b8d0] text-sm">{displayPhotos[currentIndex].caption}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Navigation Arrows */}
          {displayPhotos.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {displayPhotos.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-[#ff6b35] w-4' : 'bg-[#1a2d47]'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HappyCustomersGallery;
